// ABOUTME: Step 2 of the Amazon FBA replenishment modal: product picker and quantities.
// ABOUTME: Mirrors the Bol replenishment ProductDetails tab, without destination/warehouse polling.
import React, { useState, useEffect } from 'react';
import ProgressionTab from '../../../../theme/ProgressionBuilder/ProgressionTab';
import { useTheme } from '@emotion/react';
import Text from '../../../../theme/text/Text';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, Typography } from '@mui/material';
import { tokens } from '../../../../../theme';

import { InputDropdown } from "../../../../theme/dropdowns/InputDropdown";
import { useGetProductsSelectQuery } from '../../../../../newapi/warehouse/productSlice';
import NumberInput from '../../../../theme/inputs/NumberInput';
import { Delete } from '@mui/icons-material';


/**
 * TabContent1 Component
 *
 * Props:
 * - handleDataUpdate: Function, updates the data in the Redux state.
 * - handleNext: Function, navigates to the next tab.
 */
const Content = (props) => {
    const theme = useTheme();
    const {t} = useTranslation();
    const colors = tokens(theme.palette.mode);
    // Hydrate from the shared progression data so selections survive the unmount
    // caused by navigating back to a previous tab (e.g. to fix a rejected reference).
    const [selectedProducts, setSelectedProducts] = useState(props.data?.products ?? []);

    const { data: products } = useGetProductsSelectQuery({webshopcredentialsId: props.data?.webshopcredential, addStock: true},
        {
            skip: !props.data?.webshopcredential
        }
    );

    const addProduct = (val) => {
        setSelectedProducts(prevProducts => {
            const exists = prevProducts.some(
                p => p.id === val.id && p.sku === val.sku
            );
            if (!exists) {
                return [...prevProducts, { ...val, quantity: 1 }];
            }
            return prevProducts;
        });
    };

    const updateQuantity = (id, e) => {
        setSelectedProducts(prevProducts => {
            return prevProducts.map(product => product.id === id ? {...product, quantity: e} : product);
        });
    };

    const handleDeleleteLineRow = (id) => {
        setSelectedProducts(prevProducts => prevProducts.filter(product => product.id !== id));
    };

    // Effect for updating products in parent component
    useEffect(() => {
        props.handleDataUpdate({ products: selectedProducts });
    }, [selectedProducts]);

    useEffect(() => {
        props.setIsDisabled(selectedProducts.length === 0);
    }, [selectedProducts.length]);

    return (
        <>
            <Box
                sx={{
                    background: theme.palette.mode === "dark" ? colors.blue[100] : colors.blue[100],
                    color:theme.palette.mode === "dark" ? colors.blue[400] : colors.blue[400],
                    padding:2,
                    width:1,
                    maxWidth:500,
                    borderRadius:5,
                    mb:4,
                }}
            >
                <Typography fontWeight={"bold"} fontSize={17}>
                    {t("amazon_replenishment_product_details_title")}
                </Typography>
                <Box sx={{paddingTop:0.5}}>
                    <Typography fontSize={14}>
                        {t("amazon_replenishment_product_details_description")}
                    </Typography>
                </Box>
            </Box>


            <Box
                sx={{
                    width:1,
                    display:"flex",
                    justifyContent:"space-between",
                    flexDirection:'column',
                    gap:1,
                    pb:selectedProducts.length > 0 ? 2 : 0,
                }}
            >

                <Box
                    sx={{
                        width:200,
                    }}
                >
                    <Text>{t("choose_product")}</Text>
                </Box>

                <InputDropdown
                    key={selectedProducts.length}
                    resetOnSelection
                    searchOptions={{
                        enabled: true,
                        placeholder: t("search_for_product"),
                        searchables: ["barcodes", "sku", "description"]
                    }}
                    displayName={"combined"}
                    onChange={(selected) => addProduct(products[selected])}
                    noSelection={t("select_product_to_add")} //Custom text when nothing is selected
                    options={products ?? null}
                    selected={-1}
                    closeOnSelection
                />

            </Box>
            <Box
                sx={{
                    display:'flex',
                    flexDirection:'column',
                    gap:2,
                }}
            >
                {selectedProducts.map((product,idx) => (
                    <Box
                        key={"selectedproduct"+idx}
                        sx={{
                            width:1,
                            display:"flex",
                            alignItems:"center",
                            justifyContent:"space-between",
                            flexDirection:"column",
                            gap:1,
                            background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[50],
                            p:2,
                            borderRadius:4,
                            position:'relative',
                        }}
                    >
                        <Box
                            sx={{
                                display:"flex",
                            }}
                        >
                            <Box
                                sx={{
                                    flex:1,
                                    borderRadius:5,
                                }}
                            >
                                <Text semibold>{product.combined}</Text>
                            </Box>

                            <Box
                                sx={{
                                    position:"absolute",
                                    top:0,
                                    right:0,
                                }}
                            >
                                <IconButton type="button" color="red" onClick={() => handleDeleleteLineRow(product.id)} sx={{p:1}}>
                                    <Delete/>
                                </IconButton>
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                width:1,
                                mt:2,
                            }}
                        >
                            <NumberInput
                                value={product.quantity ?? 1}
                                change={(e) => updateQuantity(product.id, e)}
                                onlyPositive
                                />
                        </Box>
                    </Box>
                ))}
            </Box>

        </>
    );
};

const ProductDetails = (props) => {
    const { t } = useTranslation();

    const [isDisabled, setIsDisabled] = useState(true);

    return (
        <ProgressionTab
            tabId="tabProductDetails"
            subtitle={t('product_details')}
            step={2}
            maxSteps={2}
            nextDisabled={isDisabled}
        >
            <Content
                setIsDisabled={setIsDisabled}
            />
        </ProgressionTab>
    )
}

export default ProductDetails;
